import React, { useMemo } from "react";

const LineChart = ({ data, title, color = "#7C3AED" }) => {
  const width = 400;
  const height = 180;
  const margin = { top: 40, right: 30, bottom: 40, left: 30 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const uniqueId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const values = data.map(d => d.value);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const pad = range * 0.15;

    const getX = (i) => margin.left + (i / (data.length - 1)) * chartWidth;
    const getY = (val) => margin.top + chartHeight - ((val - min + pad) / (range + 2 * pad)) * chartHeight;

    const pts = data.map((d, i) => ({ x: getX(i), y: getY(d.value), value: d.value }));

    // Smoothing function (Cubic Bezier for a very smooth look)
    let path = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i];
      const p1 = pts[i + 1];
      const cp1x = p0.x + (p1.x - p0.x) / 3;
      const cp2x = p1.x - (p1.x - p0.x) / 3;
      path += ` C ${cp1x},${p0.y} ${cp2x},${p1.y} ${p1.x},${p1.y}`;
    }

    const area = `${path} L ${pts[pts.length - 1].x},${margin.top + chartHeight} L ${pts[0].x},${margin.top + chartHeight} Z`;

    return { points: pts, linePath: path, areaPath: area, labels: data.map(d => d.label) };
  }, [data, chartWidth, chartHeight, margin.left, margin.top]);

  if (!chartData) return null;

  const { points, areaPath, linePath, labels } = chartData;

  return (
    <div className="bg-white p-6 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col h-full group/chart overflow-hidden">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-1">{title}</h3>
          <div className="flex items-center gap-2">
             <span className="text-2xl font-black text-slate-800 tracking-tight">
               {points[points.length-1].value.toLocaleString()}
             </span>
             <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-full">
               +12% ↑
             </span>
          </div>
        </div>
        <div className="flex -space-x-2">
           {[...Array(3)].map((_, i) => (
             <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-[8px] font-bold text-slate-400">
               {String.fromCharCode(65 + i)}
             </div>
           ))}
        </div>
      </div>
      
      <div className="relative flex-1">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible">
          <defs>
            <linearGradient id={`gradient-${uniqueId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.4" />
              <stop offset="60%" stopColor={color} stopOpacity="0.1" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
            <filter id={`glow-${uniqueId}`} x="-20%" y="-20%" width="140%" height="140%">
               <feGaussianBlur stdDeviation="4" result="blur" />
               <feComposite in="blur" in2="SourceGraphic" operator="over" />
            </filter>
          </defs>

          {/* Background Grid */}
          <rect x={margin.left} y={margin.top} width={chartWidth} height={chartHeight} fill="#fcfdfe" rx="10" />
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <line
              key={i}
              x1={margin.left}
              y1={margin.top + chartHeight * p}
              x2={margin.left + chartWidth}
              y2={margin.top + chartHeight * p}
              stroke="#f1f5f9"
              strokeWidth="1"
            />
          ))}

          {/* Area Fill */}
          <path d={areaPath} fill={`url(#gradient-${uniqueId})`} />

          {/* Main Line with Glow */}
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: `drop-shadow(0px 4px 8px ${color}33)` }}
          />

          {/* Interactive Layer */}
          {points.map((pt, i) => (
            <g key={i} className="group/dot opacity-0 hover:opacity-100 transition-opacity duration-300">
               <line 
                x1={pt.x} y1={margin.top} 
                x2={pt.x} y2={margin.top + chartHeight} 
                stroke={color} strokeWidth="1.5" strokeDasharray="4,4" 
                className="pointer-events-none"
               />
               
               <g transform={`translate(${pt.x}, ${pt.y - 45})`}>
                 <rect x="-25" y="0" width="50" height="24" rx="12" fill="#1e293b" />
                 <text textAnchor="middle" y="15" className="text-[10px] fill-white font-black">
                   {pt.value >= 1000 ? (pt.value / 1000).toFixed(1) + 'k' : pt.value}
                 </text>
                 <path d="M -4,24 L 0,28 L 4,24 Z" fill="#1e293b" />
               </g>

               <circle
                cx={pt.x}
                cy={pt.y}
                r="8"
                fill="white"
                stroke={color}
                strokeWidth="3"
                className="cursor-pointer"
              />
            </g>
          ))}
          
          {/* Pulsing end marker */}
          <circle
            cx={points[points.length-1].x}
            cy={points[points.length-1].y}
            r="5"
            fill={color}
            className="group-hover/chart:opacity-0 transition-opacity"
          >
            <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
        </svg>

        {/* X-Axis Labels */}
        <div className="absolute left-0 right-0 bottom-1 flex justify-between px-[30px]">
           {labels.map((label, i) => (
             <span key={i} className="text-[10px] text-slate-400 font-black tracking-tighter uppercase">
               {label}
             </span>
           ))}
        </div>
      </div>
    </div>
  );
};

export default LineChart;
